import NextImage from 'next/image';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { getFishingWaterUrl } from '@domain/routes/urls';
import { EventMykissCardClicked } from '@generated/events';
import { Heading } from '../../Heading/Heading';
import { PinIcon } from '../../Icons/PinIcon';
import { MapboxAttribution } from '../../MapboxAttribution/MapboxAttribution';
import { getStaticWaterMap } from '../../Maps/mapData/staticMaps';
import { StaticMapPlaceholder } from '../../Placeholder/StaticMapPlaceholder';
import { LinkCard } from '../LinkCard/LinkCard';

export interface IFishingWaterCardProps {
  name: string;
  waterId: string;
  regionName?: string;
  countryName?: string;
  numberOfCatches: number;
  latitude: number;
  longitude: number;
  /* This is the width of the image returned by Mapbox, not the width of the component itself */
  imageWidth?: number;
  className?: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  /* Append text to the catches text */
  catchesSuffix?: string;
  /* Use if carousel shown above the fold */
  preload?: boolean;
}

const IMAGE_WIDTH = 300;
const IMAGE_HEIGHT_RATIO = 0.7;

export const FishingWaterCard = ({
  waterId,
  name,
  regionName,
  countryName,
  clickEventSource,
  clickEventPosition,
  clickEventSourceId,
  latitude,
  longitude,
  imageWidth = IMAGE_WIDTH,
  className = '',
  numberOfCatches,
  catchesSuffix,
  preload = false,
}: IFishingWaterCardProps) => {
  const { currentPage } = useAnalytics();
  const locationString = `${regionName ? `${regionName}, ` : ''}${countryName}`;

  return (
    <LinkCard
      to={getFishingWaterUrl(waterId, name)}
      eventProperties={{
        source: clickEventSource,
        target: 'fishing_water',
        target_id: waterId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      }}
      className={`rounded-md md:rounded-lg ${className}`}
    >
      <div className="relative mb-3 rounded-md pb-[70%] group-hover:shadow-md md:rounded-lg">
        <StaticMapPlaceholder />
        <NextImage
          alt={name}
          className="absolute top-0 left-0 size-full rounded-md object-center md:rounded-lg"
          src={getStaticWaterMap(
            longitude.toString(),
            latitude.toString(),
            imageWidth,
            Math.ceil(imageWidth * IMAGE_HEIGHT_RATIO),
            'hybrid',
          )}
          width={imageWidth}
          height={Math.ceil(imageWidth * IMAGE_HEIGHT_RATIO)}
          unoptimized
          preload={preload}
        />
        <MapboxAttribution isLinked={false} />
      </div>
      <Heading as="div" variant="xs" className="mb-1 md:mb-1.5">
        {name}
      </Heading>
      {regionName && countryName && (
        <div className="body-text-sm xs:mb-1 text-trench truncate md:mb-1.5">
          <div className="flex flex-row">
            <PinIcon
              width={13}
              height={13}
              className={!regionName && !countryName ? 'invisible' : 'text-trench mt-0.5 mr-1'}
            />
            {locationString}
          </div>
        </div>
      )}
      <div className="body-text-sm text-trench">
        <span className="font-semibold">{numberOfCatches}</span>
        {` logged catch${numberOfCatches === 1 ? '' : 'es'}`}
        {catchesSuffix ? ` ${catchesSuffix}` : ''}
      </div>
    </LinkCard>
  );
};
